import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, inject, OnDestroy } from "@angular/core";

import { Subscription, tap } from "rxjs";

import { RadioButtonModule } from "primeng/radiobutton";

import { OptionsService } from "@features/standby-plan/services/options.service";
import { PreviewPlanComponent } from "../preview-plan/preview-plan.component";
import { ButtonComponent, SubPageComponent } from "@shared/components";
import { GlobalService } from "@shared/services";
import { formatTimeInput } from "@shared/functions";
import { CacheId } from "@shared/enums";

@Component({
  selector: "app-options",
  standalone: true,
  imports: [SubPageComponent, ButtonComponent, RadioButtonModule, PreviewPlanComponent],
  templateUrl: "./options.component.html",
  styleUrl: "./options.component.scss",
})
export class OptionsComponent implements OnDestroy {
  globalService = inject(GlobalService);
  optionsService = inject(OptionsService);

  vmaxDefault: string = "00:00";
  originalVmaxDefault: string = "00:00";
  projDefault: string = "00:00";
  originalProjDefault: string = "00:00";

  planTypes = [
    { label: "Custom", value: "custom" },
    { label: "MAR-APR", value: "mar-apr" },
    { label: "AUG-SEP", value: "aug-sep" },
    { label: "OCT-NOV", value: "oct-nov" },
    { label: "NOV-DEC", value: "nov-dec" },
    { label: "FEB-MAR-APR", value: "feb-mar-apr" },
    { label: "OCT-NOV-DEC", value: "oct-nov-dec" },
  ];
  selectedPlanType: { label: string; value: string } = this.planTypes[0];
  previewPlanVisible: boolean = false;

  saveSettingsSubscription: Subscription | null = null;
  activatePlanSubscription: Subscription | null = null;

  constructor() {
    this.optionsService
      .getSettings()
      .pipe(
        takeUntilDestroyed(),
        tap((response) => {
          if (response.response) {
            this.vmaxDefault = response.response.vMaxDefault;
            this.originalVmaxDefault = response.response.vMaxDefault;
            this.projDefault = response.response.projDefault;
            this.originalProjDefault = response.response.projDefault;
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.saveSettingsSubscription) {
      this.saveSettingsSubscription.unsubscribe();
    }
    if (this.activatePlanSubscription) {
      this.activatePlanSubscription.unsubscribe();
    }
  }

  onKeyDown(keystroke: KeyboardEvent, type: "proj" | "vmax"): void {
    const newValue = formatTimeInput(keystroke);
    if (type === "vmax") {
      this.vmaxDefault = newValue;
    } else {
      this.projDefault = newValue;
    }
  }
  getContentHeight(): string {
    return `calc(100vh - ${this.globalService.navbarHeight()}px - 165px)`;
  }

  onPreviewClick(planType: { label: string; value: string }): void {
    this.selectedPlanType = planType;
    this.previewPlanVisible = true;
    this.updateSettings();
  }

  updateSettings(): void {
    if (this.originalProjDefault != this.projDefault || this.originalVmaxDefault != this.vmaxDefault) {
      this.saveSettingsSubscription = this.optionsService
        .saveSettings(this.vmaxDefault, this.projDefault)
        .pipe(
          tap((response) => {
            if (response.response) {
              this.originalProjDefault = this.projDefault;
              this.originalVmaxDefault = this.vmaxDefault;
              const settings = localStorage.getItem(CacheId.Settings);
              if (settings) {
                const settingsObj = JSON.parse(settings);
                settingsObj.vMaxDefault = this.vmaxDefault;
                settingsObj.projDefault = this.projDefault;
                localStorage.setItem(CacheId.Settings, JSON.stringify(settingsObj));
              }
            }
          })
        )
        .subscribe();
    }
  }

  activatePlan(): void {
    this.activatePlanSubscription = this.optionsService.activateStandbyPlan().subscribe();
  }
}
